import { MatSnackBar } from '@angular/material/snack-bar';
import { Injectable } from '@angular/core';
import { Store, Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';

import { Observable, of, combineLatest, pipe } from 'rxjs';
import { warnOnErrorWithRetryOption$ } from '@core/services/project/project-detail.effects';
import { selectLoadedProjectId } from '@core/state/ui/project/project-ui.selectors';
import { getSendableStudyTableParamsDto } from '../../state/ui/study/study-ui.selectors';
import { studySchema } from '@core/state/entities/study/study.entity';
import { map, switchMap, tap, filter } from 'rxjs';
import { take } from 'rxjs';
import { selectCurrentProject } from '@app/core/state/entities/project/project.selectors';
import { StudiesTableHttpService } from '@core/services/study-table/study-table.service';
import { StudyHttpService } from '@core/services/api-client.generated';
import { IncomingTableParamsDtoOfStudyFilters } from '../api-client.generated';
import { studiesActions } from '@core/actions/studies.actions';
import { projectDetailActions } from '@core/actions/project-detail.actions';
import { selectStudyListOnScreen } from '@core/state/ui/study/study-ui.selectors';

export const projectStudyIncomingDtoSchema = {
  data: [studySchema],
};

export const combineWithNext = <TOriginal, TIncoming>(
  combineWithObservable$: Observable<TIncoming>
) =>
  pipe(
    switchMap((originalItem: TOriginal) =>
      combineLatest([of(originalItem).pipe(take(1)), combineWithObservable$])
    )
  );

@Injectable()
export class StudyTableEffects {
  studyPageData$: Observable<Action> = createEffect(() => {
    return this._actions$.pipe(
      ofType(studiesActions.fetchStudyList, studiesActions.refreshStudyList),
      combineWithNext(
        this._store
          .select(selectLoadedProjectId)
          .pipe(filter((pId): pId is string => !!pId))
      ),
      concatLatestFrom((x) =>
        this._store.select(getSendableStudyTableParamsDto)
      ),
      switchMap(
        ([[, projectId], sendableParams]: [
          [any, string],
          IncomingTableParamsDtoOfStudyFilters,
        ]) => {
          return this._studyService
            .getTableData(projectId, sendableParams)
            .pipe(
              warnOnErrorWithRetryOption$(
                this._snackBar,
                'An error occurred while fetching studies.'
              )
            );
        }
      ),
      map((pagedStudyDetails) =>
        studiesActions.studyListUpdated(pagedStudyDetails)
      )
    );
  });

  /**
   * Reversible deletion S4a (DL-R1S-10): when a search of the open project is deleted, a study list already on screen
   * is fetched again with its current parameters, so its rows and totals drop the moved Studies without a reload. A
   * list that is not loaded is left alone: opening it fetches afresh.
   */
  refreshAfterSearchDeleted$: Observable<Action> = createEffect(() => {
    return this._actions$.pipe(
      ofType(projectDetailActions.searchDeletedNotificationReceived),
      concatLatestFrom(() => this._store.select(selectStudyListOnScreen)),
      filter(([, onScreen]) => onScreen),
      map(() => studiesActions.refreshStudyList())
    );
  });

  constructor(
    private _store: Store,
    private _actions$: Actions,
    private _snackBar: MatSnackBar,
    private _studiesTableService: StudiesTableHttpService,
    private _studyService: StudyHttpService
  ) {}
}
